<template>
  <div id="wtv" :class="{
    'wtv-theme-office': context === 'office' || context === 'td',
    'wtv-theme-pm': context === 'pm' || context === 'main-pm'
  }">
    <Error v-if="isError('Access denied')" code="Access denied" />
    <Loading v-else-if="isLoading('Initialization')" code="Initialization" />

    <Hero v-if="isInitialized" />
    <Tour v-if="tour || isLoading('Actualizing tour')" />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, computed, watch,
} from 'vue';
import { useStore } from 'vuex';
import Hero from '@/components/Hero.vue';
import Error from '@/components/Error.vue';
import Loading from '@/components/Loading.vue';
import Tour from '@/components/Tour.vue';

export default defineComponent({
  name: 'App',
  components: {
    Hero,
    Error,
    Loading,
    Tour,
  },
  setup() {
    const $store = useStore();
    const isInitialized = computed(() => $store.state.isInitialized);
    const tour = computed(() => $store.state.tour);
    const { isError, isLoading } = $store.getters;

    onMounted(() => {
      $store.dispatch('initialize').then(() => $store.dispatch('search'));
    });

    const context = computed(() => $store.getters.context);
    return {
      isInitialized,
      isError,
      isLoading,
      context,
      tour,
    };
  },
});
</script>

<style>
#wtv {
  --color-paper: #fff;
  --color-main: #06b;
  --color-second: #ef4123;
  --color-second-alt: #fff200;
  --color-third: #ef9923;
  --color-third-5: #ef992350;
  --color-alt: #0af;
  --color-alt-5: #00aaff50;
  --color-bg: #e3f6ff;
  --color-bg-dark: #cfefff;
  --color-text: #035;
  --color-text-cold: #0a3;
  --color-bg-warm: #f1abd2;
  --color-bg-cold: #43da70;
  --color-bg-cold-5: #43da7050;
  --color-text-5: #035c;
  --color-button: linear-gradient(var(--color-alt) -50%, var(--color-main));
  --color-button-hover: linear-gradient(to top,
      var(--color-second) -20%,
      var(--color-second-alt) 150%);
  font-family: 'Open Sans', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 14px;
  color: var(--color-text);
}

#wtv.wtv-theme-office {
  --color-bg: #efefef;
  --color-bg-dark: #ccc;
  --color-alt-5: #6666;
  --color-text: #222;
  --color-text-5: #222a;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
}

#wtv.wtv-theme-pm {
  --color-text: #222;
  --color-text-5: #222a;
  --color-button: #e4042a;
  --color-button-hover: #444;
  --color-main: #e4042a;
  --color-alt: #f7ce00;
  --color-alt-5: #f7ce00aa;
  --color-second-alt: #ff8b9e;
  --color-bg: #fff7ce;
  --color-bg-dark: #fff3b5;
}

#wtv.wtv-theme-pm .wtv-star:not(.wtv-star-unchecked)::before {
  color: var(--color-main) !important;
}

#wtv * {
  box-sizing: border-box;
}

.wtv-icon::before,
.wtv-icon-after::after,
.wtv-icon-left::before {
  content: '\f072';
  width: 14px;
  font: 900 normal normal 14px/1 'Font Awesome 5 Pro';
  font-size: 14px;
  text-rendering: auto;
  -webkit-font-smoothing: antialiased;
  color: var(--color-alt-5);
  transition: 0.3s ease-out;
}

.wtv-icon-left::before {
  margin: 1px 14px 0 0;
}

.wtv-layout {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--color-bg);
  /* overflow: hidden; */
}

.wtv-layout-expanded {
  padding: 12px 0;
}

.wtv-layout-row {
  width: 100%;
  max-width: 1200px;
  /* margin: -12px; */
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  justify-content: center;
}

.wtv-layout-row-short {
  max-width: 1000px;
}

.wtv-layout-column {
  max-width: 1000px;
}

.wtv-sidebar {
  padding: 12px;
  flex: 1 1 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

@media (max-width: 575px) {
  .wtv-sidebar {
    padding: 0;
  }
}

.wtv-content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 3 3 880px;
}

.wtv-small {
  font-size: 12px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.expand-enter-active,
.expand-leave-active {
  transition: height 0.1s;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: auto;
}

.wtv-button-main,
.wtv-button-second,
.wtv-button-third {
  width: 100%;
  background: var(--color-button);
  outline: none;
  padding: 7px 14px;
  border-bottom: 2px solid var(--color-alt);
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 600;
  color: var(--color-paper);
  cursor: pointer;
  border-radius: 4px;
  margin-top: 8px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wtv-button-second {
  background: var(--color-bg-dark);
  border-bottom-color: var(--color-alt-5);
  color: var(--color-main);
}

.wtv-button-third {
  background: var(--color-paper);
  color: var(--color-main);
  padding: 6px 13px;
  border: 1px solid var(--color-alt-5);
  border-bottom: 2px solid var(--color-alt-5);
  width: unset;
  display: inline-flex;
}

.wtv-button-main:hover {
  background: var(--color-button-hover);
  border-bottom: 2px solid var(--color-second-alt);
}

.wtv-button-second:hover {
  background: var(--color-bg);
  border-bottom-color: var(--color-bg-dark);
  color: var(--color-alt);
}

.wtv-button-third:hover {
  background: var(--color-bg);
}

.wtv-form-control {
  position: relative;
  width: 100%;
  max-width: 400px;
  padding: 8px;
  /* cursor: pointer; */
  flex: 2 1 160px;
  user-select: none;
}

.wtv-form-label {
  padding: 4px 12px;
  position: absolute;
  font-size: 11px;
  color: var(--color-text-5);
  font-weight: 600;
  pointer-events: none;
}

.wtv-form-input,
.wtv-form-output {
  width: 100%;
  background-color: var(--color-paper);
  outline: none;
  line-height: 16px;
  padding: 20px 12px 8px;
  border: 1px solid var(--color-alt-5);
  font-family: 'Open Sans';
  font-weight: 700;
  font-size: 16px;
  min-height: 52px;
  cursor: pointer;
  appearance: none;
}

.wtv-form-input:not([readonly]) {
  cursor: text;
}

.wtv-form-output {
  padding-top: 23px;
  white-space: nowrap;
}

.wtv-form-input.is-set,
.wtv-form-output.is-set {
  background-color: var(--color-bg);
  transition: background-color 0.3s;
}

.wtv-form-input.is-set:hover,
.wtv-form-output.is-set:hover {
  background-color: var(--color-paper);
  transition: background-color 0.3s;
}

.wtv-form-multiselect {
  padding: 8px 12px;
  background-color: var(--color-paper);
  border: 1px solid var(--color-alt-5);
  border-top: 0;
}

/* .wtv-form-multiselect .wtv-form-checkbox {
} */
input:focus {
  outline: 0;
  box-shadow: 0 0 1px var(--color-alt), 0 0 8px var(--color-bg-dark),
    1px 0 12px var(--color-bg);
}

.wtv-form-submit {
  width: 100%;
  background: var(--color-button);
  outline: none;
  padding: 14px 20px;
  border-bottom: 2px solid var(--color-alt);
  font-family: 'Open Sans';
  font-size: 16px;
  font-weight: 600;
  color: var(--color-paper);
  cursor: pointer;
  text-align: center;
  border-radius: 4px;
}

.wtv-form-submit:hover {
  background: var(--color-button-hover);
  border-bottom: 2px solid var(--color-second-alt);
}

.wtv-form-submit.is-disabled,
.wtv-form-submit.is-disabled:hover {
  background: var(--color-button-hover);
  border-bottom: 2px solid var(--color-second-alt);
  opacity: 0.5;
  cursor: wait;
}

.wtv-form-checkbox {
  cursor: pointer;
  display: flex;
  padding: 2px 0;
  transition: 0.1s;
}

.wtv-form-checkbox:before {
  content: '\f0c8';
  font-weight: 300;
  font-size: 18px;
  color: var(--color-alt-5);
  margin-right: 8px;
  transition: 0.1s;
}

.wtv-form-checkbox.wtv-checked {
  font-weight: 700;
  color: var(--color-main);
  transition: 0.1s;
}

.wtv-form-checkbox.wtv-checked:before {
  content: '\f14a';
  font-weight: 700;
  color: var(--color-alt);
  transition: 0.1s;
}

.wtv-form-control-hotel .wtv-form-checkbox {
  text-transform: capitalize;
}

.wtv-price {
  font-size: 22px;
  font-weight: 700;
}

.wtv-price .wtv-small {
  font-size: 15px;
  font-weight: 600;
}

.wtv-stick-to-right {
  right: 0;
  left: unset;
}

.wtv-relation {
  position: fixed;
  z-index: 10000;
  background-color: var(--color-bg);
  border-top: 8px solid var(--color-alt-5);
  padding: 8px 18px;
  bottom: 15px;
  right: 15px;
  cursor: pointer;
}

.wtv-relation:after {
  content: '\f00d';
  font-size: 18px;
  color: var(--color-alt);
  margin-left: 12px;
}

.wtv-lower {
  font-size: 0.7em;
}

.wtv-nowrap {
  white-space: nowrap;
}

.wtv-nomargin {
  margin: 0;
}

@keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }

  100% {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}

/* links */
.wtv-cell-content-text a {
  position: relative;
  text-decoration: unset;
  color: var(--color-alt);
  cursor: pointer;
}

.wtv-cell-content-text a::before {
  content: '\f05a';
  width: 14px;
  font: 900 normal normal 14px/1 'Font Awesome 5 Pro';
  font-size: 14px;
  margin-right: 4px;
  text-rendering: auto;
  -webkit-font-smoothing: antialiased;
  color: var(--color-alt-5);
  transition: 0.3s ease-out;
}

.wtv-cell-content-text a .wtv-link-description {
  color: var(--color-text);
  padding: 4px 12px;
  position: absolute;
  width: 42vw;
  background-color: var(--color-paper);
  z-index: 3;
  border-radius: 2px;
  box-shadow: 0 0 1px var(--color-alt), 0 0 8px var(--color-bg-dark),
    1px 0 12px var(--color-bg);
}

@media (max-width: 575px) {
  .wtv-cell-content-text a .wtv-link-description {
    width: 84vw;
  }
}

/* icons */
.wtv-icon-more,
.wtv-icon-less {
  margin-bottom: 4px;
  /* margin-right: 4px; */
}

.wtv-icon-more::before,
.wtv-icon-less::before {
  display: inline-flex;
  width: 24px;
  height: 24px;
  content: '\f13a';
  font-size: 24px;
  color: var(--color-paper);
}

.wtv-icon-less::before {

  transform: rotate(180deg);
}

.wtv-icon-adults::before {
  content: '\f2bd';
}

.wtv-icon-nights::before {
  content: '\f186';
}

.wtv-icon-flydate::before {
  content: '\f783';
}

/* .wtv-icon-text {
  flex: 1 0 calc(100% - 42px);
} */
.wtv-icon-text::before {
  content: unset;
}

.wtv-cell-more-item.wtv-icon::before {
  color: var(--color-third);
}
</style>
